import type { LivingBackground } from "@/utils/living-background";

const VERTEX = `attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }`;

// Every motion term uses a whole multiple of u_phase, so one cycle (u_phase 0 → 2π) ends where it began.
const FRAGMENT = `precision mediump float;
uniform vec2 u_res;
uniform float u_phase;
uniform vec3 u_c1;
uniform vec3 u_c2;
uniform vec3 u_c3;
uniform float u_strength;
uniform float u_size;
uniform float u_dir;
uniform float u_soft;
uniform float u_effect;
vec3 pick(float i) { return i < 0.5 ? u_c1 : (i < 1.5 ? u_c2 : u_c3); }
void main() {
  vec2 uv = gl_FragCoord.xy / u_res;
  float aspect = u_res.x / u_res.y;
  vec3 colour = vec3(0.0);
  float alpha = 0.0;
  for (int index = 0; index < 3; index++) {
    float i = float(index);
    float a;
    if (u_effect < 0.5) {
      vec2 q = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);
      q = vec2(cos(u_dir) * q.x + sin(u_dir) * q.y, cos(u_dir) * q.y - sin(u_dir) * q.x);
      float wave = sin(q.x * 3.0 / u_size + u_phase + i * 2.1) * 0.12
        + sin(q.x * 7.0 / u_size - 2.0 * u_phase + i) * 0.03;
      float distance = abs(fract((q.y + wave) * 9.0 / u_size + i / 3.0) - 0.5);
      a = (1.0 - smoothstep(0.0, 0.03 + u_soft * 0.2, distance))
        * (0.65 + 0.35 * sin(q.x * 2.0 + u_phase + i));
    } else {
      float crest = (0.16 + i * 0.09) * u_size
        + 0.035 * u_size * sin(uv.x * aspect * (5.0 - i) / u_size + (i + 1.0) * u_phase + i * 1.7);
      a = 1.0 - smoothstep(crest - 0.004 - u_soft * 0.08, crest + 0.004 + u_soft * 0.08, uv.y);
    }
    a *= u_strength;
    colour = pick(i) * a + colour * (1.0 - a);
    alpha = a + alpha * (1.0 - a);
  }
  gl_FragColor = vec4(colour, alpha);
}`;

const SLOTS = ["--re-living-1", "--re-living-2", "--re-living-3"] as const;

/** The colour a custom property shows, as 0–1 channels; the browser resolves palette references and color-mix(). */
function channels(
  element: Element,
  property: string,
): [number, number, number] {
  const probe = element.ownerDocument.createElement("span");
  probe.style.color = `var(${property})`;
  element.appendChild(probe);
  const value = getComputedStyle(probe).color;
  probe.remove();
  const numbers = (value.match(/[\d.]+/g) ?? []).map(Number);
  if (value.startsWith("color(srgb"))
    return [numbers[0] ?? 0, numbers[1] ?? 0, numbers[2] ?? 0];
  return [
    (numbers[0] ?? 0) / 255,
    (numbers[1] ?? 0) / 255,
    (numbers[2] ?? 0) / 255,
  ];
}

function program(gl: WebGLRenderingContext) {
  const shader = (type: number, source: string) => {
    const compiled = gl.createShader(type)!;
    gl.shaderSource(compiled, source);
    gl.compileShader(compiled);
    return gl.getShaderParameter(compiled, gl.COMPILE_STATUS) ? compiled : null;
  };
  const vertex = shader(gl.VERTEX_SHADER, VERTEX);
  const fragment = shader(gl.FRAGMENT_SHADER, FRAGMENT);
  if (!vertex || !fragment) return null;
  const linked = gl.createProgram()!;
  gl.attachShader(linked, vertex);
  gl.attachShader(linked, fragment);
  gl.linkProgram(linked);
  return gl.getProgramParameter(linked, gl.LINK_STATUS) ? linked : null;
}

/**
 * Draws Silke or Bølger into `canvas` until the returned stop is called; undefined when WebGL will not run it, so the
 * caller keeps the CSS version. It stands still while its host is paused or idle, and `onLost` reports a lost context.
 */
export function startLivingShader(
  canvas: HTMLCanvasElement,
  living: LivingBackground,
  onLost: () => void,
): (() => void) | undefined {
  const gl = canvas.getContext("webgl", { antialias: false });
  const linked = gl && program(gl);
  if (!gl || !linked) return undefined;
  gl.useProgram(linked);
  const buffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW,
  );
  const position = gl.getAttribLocation(linked, "p");
  gl.enableVertexAttribArray(position);
  gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
  const uniform = (name: string) => gl.getUniformLocation(linked, name);
  gl.uniform1f(uniform("u_strength"), living.strength / 100);
  gl.uniform1f(uniform("u_size"), living.size / 100);
  gl.uniform1f(uniform("u_dir"), (living.direction * Math.PI) / 180);
  gl.uniform1f(uniform("u_soft"), living.softness / 120);
  gl.uniform1f(uniform("u_effect"), living.effect === "silke" ? 0 : 1);
  const colours = () =>
    SLOTS.forEach((slot, index) =>
      gl.uniform3fv(
        uniform(`u_c${index + 1}`),
        channels(canvas.parentElement ?? canvas, slot),
      ),
    );
  colours();
  const scheme = new MutationObserver(colours);
  scheme.observe(canvas.ownerDocument.documentElement, {
    attributes: true,
    attributeFilter: ["data-re-scheme"],
  });
  const scale =
    living.quality === "hoej"
      ? Math.min(window.devicePixelRatio || 1, 2)
      : 0.75;
  const resize = () => {
    canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale));
    canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(uniform("u_res"), canvas.width, canvas.height);
  };
  resize();
  const sized = new ResizeObserver(resize);
  sized.observe(canvas);
  const interval = living.quality === "hoej" ? 0 : 1000 / 30;
  let elapsed = 0;
  let last: number | undefined;
  let drawn = -Infinity;
  let frame = requestAnimationFrame(function tick(now) {
    frame = requestAnimationFrame(tick);
    const still = canvas.closest(
      "[data-re-living-paused], [data-re-living-idle]",
    );
    if (!still && last !== undefined) elapsed += now - last;
    last = now;
    if (still || now - drawn < interval) return;
    drawn = now;
    gl.uniform1f(
      uniform("u_phase"),
      ((elapsed / 1000 / living.speed) % 1) * 2 * Math.PI,
    );
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  });
  const lost = (event: Event) => {
    event.preventDefault();
    onLost();
  };
  canvas.addEventListener("webglcontextlost", lost);
  return () => {
    cancelAnimationFrame(frame);
    sized.disconnect();
    scheme.disconnect();
    canvas.removeEventListener("webglcontextlost", lost);
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  };
}
